import { convertSearchParamsToObject } from "helpers";
import {
  DATE_RANGE_FILTER_SEARCH_PARAMETER,
  EMPTY_TRANSACTIONS_FILTER_FORM_VALUES,
  STATUS_FILTER_SEARCH_PARAMETER,
  TOTAL_FROM_FILTER_SEARCH_PARAMETER,
  TOTAL_TO_FILTER_SEARCH_PARAMETER
} from "modules/financial";
import { useGetOrdersFilterLimits } from "modules/orders";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Button, RangeFilterField } from "ui";

import { DateRangeBlock, StatusBlock } from "./FormBlocks";
import {
  TransactionsFilterFormProps,
  FilterFormValues
} from "./TransactionsFilterForm.types";

import styles from "./TransactionsFilterForm.module.scss";

const TransactionsFilterForm = ({
  formId,
  onSubmit,
  isLoading
}: TransactionsFilterFormProps) => {
  const { t } = useTranslation();

  const { filterLimits } = useGetOrdersFilterLimits();

  const [searchParams, setSearchParams] = useSearchParams();

  const formMethods = useForm<FilterFormValues>({
    defaultValues: EMPTY_TRANSACTIONS_FILTER_FORM_VALUES
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const newValues = convertSearchParamsToObject(searchParams);

    if (newValues?.statuses && !Array.isArray(newValues.statuses)) {
      newValues.statuses = [newValues.statuses];
    }

    const handledNewValues = {
      ...EMPTY_TRANSACTIONS_FILTER_FORM_VALUES,
      ...newValues
    };

    reset(handledNewValues);
  }, [reset, searchParams]);

  const clearFilterForm = () => {
    searchParams.delete(STATUS_FILTER_SEARCH_PARAMETER);
    searchParams.delete(TOTAL_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(TOTAL_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(DATE_RANGE_FILTER_SEARCH_PARAMETER);

    setSearchParams(searchParams);
    reset();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        id={formId}
        className={styles["filter-form"]}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles["filter-form__header"]}>
          <div className={styles["filter-form__header--title"]}>
            {t("Filter by")}
          </div>
          <div
            onClick={clearFilterForm}
            className={styles["filter-form__header--clear"]}
          >
            {t("Clear all filters")}
          </div>
        </div>

        <StatusBlock />

        <div className={styles["filter-form__separator"]} />

        {filterLimits && (
          <RangeFilterField
            title={`${t("Total")} (FCFA)`}
            fromFieldKey={TOTAL_FROM_FILTER_SEARCH_PARAMETER}
            toFieldKey={TOTAL_TO_FILTER_SEARCH_PARAMETER}
            minTotal={0}
            maxTotal={filterLimits?.maxOrderTotal}
          />
        )}

        <div className={styles["filter-form__separator"]} />

        <DateRangeBlock />

        <div className={styles["filter-form__separator"]} />

        <Button
          type="submit"
          loading={isLoading}
          className={styles["filter-form__button"]}
        >
          {t("Apply")}
        </Button>
      </form>
    </FormProvider>
  );
};

export default TransactionsFilterForm;
